import { useContext, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import { useSiteGetApi } from '@/common/api/modules/site'
import { useWidgetDeleteApi } from '@/common/api/modules/widget'
import {
  CreateWidgetResponse,
  CreateWidgetVars,
  UpdateWidgetResponse,
  UpdateWidgetVars
} from '@/common/api/params/widget'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import {
  BUNDLE_CSS_CLASSES_ANCHOR,
  BUNDLE_MAX_ITEMS_CHOICES,
  BUNDLE_MAX_ITEMS_MAX,
  BUNDLE_MAX_ITEMS_MIN,
  WIDGET_STATUS_CHOICES,
  bundleLayoutUnavailableReason,
  widgetDisplayLogicContentType,
  widgetDisplayLogicName,
  widgetLayoutTypeName
} from '@/common/app/modules/recommend/widget'
import { tagsPath, widgetEditPath, widgetListPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'
import { registerNumberOption } from '@/common/form/register'
import {
  Widget,
  WidgetContentType,
  WidgetDisplayLogic,
  WidgetLayoutType
} from '@/common/model/recommend/widget'
import { preventKeyDown } from '@/common/util/keyboard'
import { LocalStorage, loadStorage } from '@/common/util/storage'

import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import GroupButton from '@/components/view/atoms/button/GroupButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import Side from '@/components/view/layouts/page/Side'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  WidgetFormSchemaType,
  convertWidgetSchemaToVars,
  convertWidgetToSchema,
  makeDefaultWidgetSchema,
  widgetNameLabelMapping,
  widgetSchema
} from '@/components/container/form/modules/recommend/widget/widgetSchema'
import FormCheckBox from '@/components/container/form/widgets/FormCheckBox'
import FormDisclosureSelector from '@/components/container/form/widgets/FormDisclosureSelector'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormRadioButton from '@/components/container/form/widgets/FormRadioButton'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import FormTextArea from '@/components/container/form/widgets/FormTextArea'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import { WidgetDisplayLogicModalContext } from '@/components/container/modal/widget/displayLogic/WidgetDisplayLogicModalProvider'
import { VisualCssEditorModalContext } from '@/components/container/modal/widget/visualCssEditor/VisualCssEditorModalProvider'

const authHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${loadStorage(LocalStorage.AUTH_TOKEN) || ''}`
})

interface Props {
  widget?: Widget
  // 複製元（新規作成時に値を初期反映する。識別子は引き継がず新規作成として扱う）
  copySource?: Widget
  onRefetch?(): void
}

const CONTENT_TYPE_CHOICES = [
  {
    name: '商品レコメンド',
    value: WidgetContentType.PRODUCT,
    description: (
      <>
        ユーザーの行動履歴をもとに「あなたへのおすすめ」を表示したり、
        <br />
        「この商品に似た商品」や「この商品を買った人はこちらも購入しています」を表示します。
      </>
    )
  },
  {
    name: '記事レコメンド',
    value: WidgetContentType.ARTICLE,
    description: (
      <>
        閲覧数が多い記事や、今読んでいる記事に内容が近い記事、
        <br />
        「この記事を読んだユーザーはこちらも読んでいます」を表示します。
      </>
    )
  }
]

const WidgetForm = ({ widget, copySource, onRefetch }: Props) => {
  const navigate = useNavigate()
  const siteId = useSiteId()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    reset,
    getValues,
    setValue,
    setError
  } = useForm<WidgetFormSchemaType>({
    resolver: zodResolver(widgetSchema),
    values: widget
      ? convertWidgetToSchema(widget)
      : copySource
        ? { ...convertWidgetToSchema(copySource), id: undefined, siteId }
        : makeDefaultWidgetSchema(siteId),
    mode: 'onChange'
  })

  const useTitle = useWatch({ control, name: 'useTitle' })
  const contentType = useWatch({ control, name: 'contentType' })
  const displayLogic = useWatch({ control, name: 'displayLogic' })
  const layoutType = useWatch({ control, name: 'layoutType' })
  const maxItems = useWatch({ control, name: 'maxItems' })

  const bundleReason = bundleLayoutUnavailableReason(contentType, displayLogic)
  const isBundle = layoutType === WidgetLayoutType.BUNDLE
  const layoutChoices = Object.values(WidgetLayoutType).map((v) => ({
    name: widgetLayoutTypeName(v),
    value: v,
    // 条件を満たさない間はバンドルを選べない（選択済みの場合はレイアウト欄の下に理由を出す）
    disabled: v === WidgetLayoutType.BUNDLE && bundleReason !== null
  }))
  const layoutRegister = register('layoutType', {
    onChange: (e) => {
      // 4以上の表示数のままバンドルへ切り替えたら、選べる上限（3）にそろえる
      const current = getValues('maxItems')
      if (
        e.target.value === WidgetLayoutType.BUNDLE &&
        !(current >= BUNDLE_MAX_ITEMS_MIN && current <= BUNDLE_MAX_ITEMS_MAX)
      ) {
        setValue('maxItems', BUNDLE_MAX_ITEMS_MAX, { shouldDirty: true, shouldValidate: true })
      }
    }
  })

  const { showModal: showVisualCssModal } = useContext(VisualCssEditorModalContext)

  const onClickAdjust = () => {
    const titleTemplate = getValues('titleTemplate')
    showVisualCssModal({
      siteId,
      widgetPageId: getValues('widgetPageId'),
      layoutType: getValues('layoutType'),
      titleTemplate,
      maxItems: getValues('maxItems'),
      useTitle: !!titleTemplate,
      currentCss: getValues('customCss'),
      onApply: (values) => {
        setValue('customCss', values.custom_css, { shouldDirty: true })
        setValue('layoutType', values.layout_type, { shouldDirty: true })
        setValue('maxItems', values.max_items, { shouldDirty: true })
        setValue('useTitle', values.use_title, { shouldDirty: true })
        setValue('titleTemplate', values.use_title ? values.title_template : '', {
          shouldDirty: true
        })
        setValue('widgetPageId', values.widget_page_id, { shouldDirty: true })
      }
    })
  }

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, widgetNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!widget })

  const { trigger } = useCallMutation<
    CreateWidgetResponse | UpdateWidgetResponse,
    CreateWidgetVars | UpdateWidgetVars
  >(
    widget ? ApiPath.UPDATE_WIDGET : ApiPath.CREATE_WIDGET,
    {
      onSuccess: (response) => {
        showSaved(response.name)
        widget && onRefetch ? onRefetch() : navigate(widgetEditPath(siteId, response.id))
      }
    },
    widget ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<WidgetFormSchemaType> = (data) => {
    // サイトがW2連動でないときのバンドルはスキーマで検出できないため、送信前に止める
    if (data.layoutType === WidgetLayoutType.BUNDLE && bundleReason) {
      setError('layoutType', { message: bundleReason })
      return
    }
    return trigger(convertWidgetSchemaToVars(data))
  }

  const { onClickDelete } = useWidgetDeleteApi(widget ?? ({} as Widget), () =>
    navigate(widgetListPath(siteId))
  )

  const { showModal } = useContext(WidgetDisplayLogicModalContext)

  // 2026-10-06 追加: メール配信用のロジックは、メールレコメンド（サイトの enable_mail_reco）が ON のサイトでだけ選べる。
  // 既に mail のパーツは、OFF のサイトでも今の値が選択肢から消えないよう残す
  const { data: site } = useSiteGetApi({ site_id: siteId })
  const mailLogicSelectable =
    !!site?.enable_mail_reco || widget?.display_logic === WidgetDisplayLogic.MAIL

  const displayLogicChoices = Object.values(WidgetDisplayLogic)
    .filter((logic) => contentType === widgetDisplayLogicContentType(logic))
    .filter((logic) => logic !== WidgetDisplayLogic.MAIL || mailLogicSelectable)
    .map((v) => ({
      name: widgetDisplayLogicName(v),
      value: v
    }))

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={widget ? widget.name : 'レコメンドパーツを新規作成する'}
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {widget ? '保存する' : '登録する'}
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={widget ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={widget ? '未反映の変更があります' : '登録されていない新しいレコメンドパーツ'}
          onCancel={() =>
            reset(widget ? convertWidgetToSchema(widget) : makeDefaultWidgetSchema(siteId))
          }
          loading={isSubmitting}
          withSide
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <ContentSideFrame>
        <Content type={ContentType.TWO_COLUMNS_LEFT}>
          <Area>
            <Heading>基本設定</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel id='contentType'>コンテンツタイプ</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormRadioButton
                    control={control}
                    name={'contentType'}
                    gap={12}
                    choices={CONTENT_TYPE_CHOICES}
                    input
                  />
                  <FormError message={errors.contentType?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel required id='name'>
                  レコメンド
                  <br />
                  パーツ名
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register('name')}
                    fieldType={FieldType.REQUIRED}
                    error={!!errors.name?.message}
                    size={TextBoxSize.LARGE}
                    placeholder='商品ページBB'
                  />
                  <FormError message={errors.name?.message} />
                </FormRow>
              </FormRow>
            </AreaContent>
          </Area>

          <Area noBorder>
            <Heading
              rightNode={
                <TextButton onClick={() => showModal({})}>
                  <Icon type={IconType.HELP} color={IconColor.LINK} line />
                  レコメンドロジックについて
                </TextButton>
              }
            >
              表示デザイン設定
            </Heading>
            <AreaContent>
              <FormRow>
                <FormLabel required id='displayLogic'>
                  表示に使うレコメンドロジック
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormSelector
                    register={register('displayLogic')}
                    choices={displayLogicChoices}
                    error={!!errors.displayLogic?.message}
                  />
                  <FormError message={errors.displayLogic?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='titleTemplate'>表示レコメンドスタイル</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormRow gap={16} size={TextBoxSize.FULL} align='center'>
                    <FormCheckBox
                      control={control}
                      name={'useTitle'}
                      type={CheckBoxType.TOGGLE_TEXT}
                      toggleText={{ left: '利用', right: '不要' }}
                    />
                    <FormTextField
                      register={register('titleTemplate')}
                      error={!!errors.titleTemplate?.message}
                      size={TextBoxSize.REMNANT}
                      placeholder='この商品を買った人はこちらも購入しています'
                      disabled={!useTitle}
                    />
                  </FormRow>
                  <FormError message={errors.titleTemplate?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='layoutType'>レイアウト</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormSelector
                    register={layoutRegister}
                    choices={layoutChoices}
                    error={!!errors.layoutType?.message || (isBundle && !!bundleReason)}
                  />
                  <FormError
                    message={
                      errors.layoutType?.message || (isBundle ? bundleReason : null) || undefined
                    }
                  />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='maxItems'>表示数</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  {isBundle ? (
                    <>
                      {/* バンドルの表示数は主商品に加えて並べる併売候補の数（1〜3） */}
                      <GroupButton
                        choices={BUNDLE_MAX_ITEMS_CHOICES}
                        value={String(maxItems)}
                        onSelect={(v) =>
                          setValue('maxItems', Number(v), {
                            shouldDirty: true,
                            shouldValidate: true
                          })
                        }
                      />
                      <FormCaption size={12}>主商品に加えて並べる商品の数です。</FormCaption>
                    </>
                  ) : (
                    <FormTextField
                      register={register('maxItems', registerNumberOption)}
                      size={TextBoxSize.X_SMALL}
                      type={FormTextFieldType.NUMBER}
                      error={!!errors.maxItems?.message}
                    />
                  )}
                  <FormError message={errors.maxItems?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='customCss'>カスタムCSS</FormLabel>
                <FormRow direction={'vertical'} gap={8}>
                  <FormRow direction={'vertical'} gap={2}>
                    <FormTextArea
                      register={register('customCss')}
                      control={control}
                      name='customCss'
                      error={!!errors.customCss?.message}
                      placeholder={'.w2sr-widget{\n    font-size: 16px;\n}'}
                    />
                    <FormError message={errors.customCss?.message} />
                  </FormRow>
                  {isBundle && (
                    <TextButton
                      link={`${tagsPath(siteId)}?tab=display#${BUNDLE_CSS_CLASSES_ANCHOR}`}
                      newTab
                      size={12}
                    >
                      バンドルで使えるクラス名を見る
                    </TextButton>
                  )}
                  <AiButton onClick={onClickAdjust}>デザインをAIと調整</AiButton>
                </FormRow>
              </FormRow>
            </AreaContent>
          </Area>
        </Content>
        <Side>
          <Area>
            <Heading>利用状況</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel id='status' fixWidth={80}>
                  ステータス
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormDisclosureSelector
                    control={control}
                    name={'status'}
                    choices={WIDGET_STATUS_CHOICES}
                  />
                  <FormError message={errors.status?.message} />
                </FormRow>
              </FormRow>
            </AreaContent>
          </Area>
        </Side>
      </ContentSideFrame>
      <PageBottom between={!!widget}>
        {widget && (
          <Button color={ButtonColor.CRITICAL} size='large' onClick={onClickDelete}>
            このパーツを削除する
          </Button>
        )}
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {widget ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default WidgetForm
